import { useId, type ReactNode } from "react";
import { cn } from "../../lib/utils.js";
import {
  contactFormFieldInputClass,
  contactFormFieldLabelClass,
  contactSheetSectionClass,
  contactSheetSectionLegendClass,
  contactViewFieldLabelClass,
  contactViewFieldValueClass,
  contactViewSectionClass,
  contactViewSectionIconClass,
} from "./contact-sheet-presentation.js";

export function ContactFormField({
  id,
  label,
  type = "text",
  value,
  placeholder,
  disabled,
  onChange,
  describedBy,
  invalid,
}: {
  id: string;
  label: string;
  type?: "text" | "email" | "tel";
  value: string;
  placeholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
  describedBy?: string;
  invalid?: boolean;
}): React.JSX.Element {
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className={contactFormFieldLabelClass}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        dir={type === "email" || type === "tel" ? "ltr" : undefined}
        className={cn(
          contactFormFieldInputClass,
          (type === "email" || type === "tel") && "[unicode-bidi:isolate]",
        )}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={invalid}
        aria-describedby={describedBy}
      />
    </div>
  );
}

export function ContactFormSection({
  legend,
  children,
  className,
}: {
  legend: string;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  const legendId = useId();

  return (
    <section className={cn(contactSheetSectionClass, className)} aria-labelledby={legendId}>
      <h3 id={legendId} className={contactSheetSectionLegendClass}>
        {legend}
      </h3>
      {children}
    </section>
  );
}

export function ContactViewField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid gap-0.5">
      <dt className={contactViewFieldLabelClass}>{label}</dt>
      <dd className={contactViewFieldValueClass}>{children}</dd>
    </div>
  );
}

export function ContactViewSection({
  legend,
  icon,
  children,
  className,
}: {
  legend: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  const legendId = useId();

  return (
    <section className={cn(contactViewSectionClass, className)} aria-labelledby={legendId}>
      <h3
        id={legendId}
        className={cn(contactSheetSectionLegendClass, "mb-2 flex items-center gap-1.5")}
      >
        {icon ? (
          <span className={contactViewSectionIconClass} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {legend}
      </h3>
      {children}
    </section>
  );
}
